import React, { useState, useEffect } from 'react';
import { resolverRotaInicial } from '../../../shared/rota-inicial';
import { decodeJwtPayload } from '../../../utils/jwt';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Button, Intent } from '@blueprintjs/core';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import {
  clearAccessToken,
  setAccessToken,
  setRefreshToken,
  setCodigoVoluntario,
  setUserType,
  setMustChangePassword,
} from '../reducer';

import { loginUser } from '../../../api/authApi';
import { checkAuthentication } from '../../../auth/authService';

import logoBackground from './css/img/Atual Clube - Marca_Laranja 1.svg';
import LogoSoac from '../../../components/logo-soac/LogoSoac';
import eyeIcon from './css/img/eye.svg';
import closedEyeIcon from './css/img/closed-eye.svg';

import './css/auth.css';
import './css/register.css';
import { lerTokenDoCookie } from '../../../shared/cookie-da-sessao';

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [username, setUsernameState] = useState<string>('');
  const [password, setPasswordState] = useState<string>('');
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  useEffect(() => {
    const checkUserAuthentication = async () => {
      const token = lerTokenDoCookie() || localStorage.getItem('authToken');

      if (token) {
        const isAuthenticated = await checkAuthentication();

        if (isAuthenticated) {
          // at-81: um resolvedor só decide a tela inicial (grupo > papel > Home). Antes havia
          // uma cópia aqui, outra no submit e outra no callback do Google — todas divergentes,
          // e uma delas apontava para rota inexistente (BUG-V69).
          navigate(resolverRotaInicial(decodeJwtPayload(token)));
        }
      }
    };

    checkUserAuthentication();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (!errorParam) return;

    let friendlyMessage: string;
    if (errorParam === 'google_auth_failed') {
      friendlyMessage = 'Token não encontrado após autenticação com Google. Tente novamente.';
    } else if (errorParam === 'token_decode_failed') {
      friendlyMessage = 'Erro ao decodificar o token de autenticação. Contate o suporte.';
    } else {
      friendlyMessage = decodeURIComponent(errorParam);
    }

    const timer = setTimeout(() => {
      toast.error(friendlyMessage);
      navigate('/login', { replace: true });
    }, 100);

    return () => clearTimeout(timer);
  }, [searchParams, navigate]);

  const handleLogin = async (event: React.FormEvent) => {
    event.preventDefault();
    setIsLoading(true);

    try {
      dispatch(clearAccessToken());

      const { token, refreshToken, user } = await loginUser(username, password);

      dispatch(setAccessToken(token));
      dispatch(setRefreshToken(refreshToken));
      dispatch(setCodigoVoluntario(user.codigoVoluntario));
      dispatch(setUserType(user.userType));
      dispatch(setMustChangePassword(user.mustChangePassword));

      localStorage.setItem('authToken', token);
      localStorage.setItem('refreshToken', refreshToken);
      sessionStorage.removeItem('passwordChangeDeferred');

      toast.success('Login realizado com sucesso!');

      // A resposta do login já traz `papeis` e `telaInicial` resolvidos pelo backend.
      navigate(resolverRotaInicial(user));
    } catch (error) {
      console.error('Erro ao fazer login', error);

      const message =
        error instanceof Error
          ? error.message
          : 'Falha ao realizar login. Verifique suas credenciais.';

      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword((prev) => !prev);
  };

  return (
    <div className="main-containt">
      <div className="logo-background">
        <img className="logo-background-atual" src={logoBackground} alt="logo de fundo" />
      </div>

      <div className="form-container-auth">
        {/* at-131: só a marca do sistema, em branco — a porca laranja sumia no fundo laranja. */}
        <LogoSoac tamanho={48} corDaPorca="#ffffff" corDoFundo="#ea5d0e" className="logo-soac--login" />

        <form className="form-auth" onSubmit={handleLogin}>
          <div className="auth-div">
            <div className="auth-title">
              <h1 className="h1-auth">Login</h1>
              <p className="p-auth">Por favor, entre com seu email e senha.</p>
            </div>

            <div className="auth-fields">
              <p>Usuário</p>

              <div className="nice-form-group auth">
                <input
                  id="username"
                  name="username"
                  className="nice-input"
                  type="text"
                  value={username}
                  onChange={(e) => setUsernameState(e.target.value)}
                  placeholder="exemplo@atualclube.org"
                  required
                />
              </div>

              <p>Senha</p>

              <div className="nice-form-group auth">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  name="password"
                  className="nice-input"
                  value={password}
                  onChange={(e) => setPasswordState(e.target.value)}
                  placeholder="Senha"
                  autoComplete="new-password"
                  required
                />

                <img
                  className="icon"
                  src={showPassword ? closedEyeIcon : eyeIcon}
                  alt="icone de olho"
                  onClick={togglePasswordVisibility}
                />
              </div>
            </div>

            <div className="forgot-password">
              <Link to="/forgot-password">Esqueci a minha senha</Link>
            </div>

            <div className="button-div auth">
              <Button type="submit" intent={Intent.PRIMARY} disabled={isLoading}>
                {isLoading ? 'Carregando...' : 'Entrar'}
              </Button>
            </div>

            <div className="auth-separator">
              <span>ou</span>
            </div>

            <button
              type="button"
              className="google-login-btn"
              onClick={() => {
                window.location.href = `${process.env.REACT_APP_BACKEND_URL}/auth/google`;
              }}
            >
              <svg width="18" height="18" viewBox="0 0 18 18" style={{ marginRight: '8px' }}>
                <path
                  fill="#4285F4"
                  d="M17.64 9.2c0-.63-.06-1.25-.16-1.84H9v3.47h4.84a4.14 4.14 0 0 1-1.8 2.71v2.26h2.91c1.7-1.56 2.69-3.86 2.69-6.6z"
                />
                <path
                  fill="#34A853"
                  d="M9 18c2.43 0 4.47-.8 5.96-2.2l-2.91-2.26c-.8.54-1.83.86-3.05.86-2.34 0-4.33-1.58-5.04-3.7H.95v2.33A9 9 0 0 0 9 18z"
                />
                <path
                  fill="#FBBC05"
                  d="M3.96 10.7a5.4 5.4 0 0 1 0-3.4V4.97H.95a9 9 0 0 0 0 8.06l3.01-2.33z"
                />
                <path
                  fill="#EA4335"
                  d="M9 3.58c1.32 0 2.5.45 3.44 1.35L15 2.1A9 9 0 0 0 .95 4.97l3.01 2.33c.71-2.12 2.7-3.72 5.04-3.72z"
                />
              </svg>
              Entrar com Google
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default Login;
